// web/src/components/CookieBanner.tsx
// UK ICO-compliant cookie consent banner.
//
// Storage we use (essential):
//   - localStorage for auth tokens — first-party, necessary for the service
//
// Non-essential we set ONLY on consent (added 2026-05-26):
//   - Google Analytics 4 (gtag.js with Consent Mode v2). Script loads on
//     every page but `analytics_storage` defaults to 'denied'; only flipped
//     to 'granted' when this banner records `accepted`.
//
// Persisted decision in localStorage under `mfj-cookie-consent`:
//   - 'accepted' | 'declined' | absent
// Banner renders only when the key is absent. Hidden permanently after a
// decision. On every page load `applyConsentToAnalytics()` re-syncs the
// stored decision into gtag so returning visitors stay correctly tracked
// or correctly anonymous.

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'mfj-cookie-consent';

type Decision = 'accepted' | 'declined';

// Augment Window so we can call window.gtag without TS complaints. gtag is
// defined inline in web/index.html before the React bundle loads.
type GtagFn = (...args: unknown[]) => void;
declare global {
  interface Window {
    gtag?: GtagFn;
  }
}

export function getCookieConsent(): Decision | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'accepted' || v === 'declined' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Push the user's stored consent decision into the gtag layer. Safe to call
 * on every page load (idempotent — gtag just keeps the latest state). Used
 * by main.tsx so returning users with a previous 'accepted' decision get
 * tracked from the very first event of the session, not after a click.
 */
export function applyConsentToAnalytics(): void {
  if (typeof window === 'undefined') return;
  const decision = getCookieConsent();
  if (!decision) return; // First-time visitor — leave default 'denied'.
  // Only fire if gtag is loaded (it might not be in tests / SSR).
  if (typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: decision === 'accepted' ? 'granted' : 'denied',
  });
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer the visibility check to the next tick so SSR (if we ever add it)
    // doesn't get a hydration mismatch.
    const t = window.setTimeout(() => {
      setVisible(getCookieConsent() === null);
    }, 0);
    return () => window.clearTimeout(t);
  }, []);

  const decide = (d: Decision) => {
    try { window.localStorage.setItem(STORAGE_KEY, d); } catch { /* ignore */ }
    // Push the decision into gtag immediately so the very next event in
    // this session is correctly granted (or stays denied). Without this
    // an 'accepted' user wouldn't be tracked until their next page load.
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: d === 'accepted' ? 'granted' : 'denied',
      });
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="mfj-cookie-title"
      aria-describedby="mfj-cookie-body"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 'var(--z-toast)' as unknown as number,
        maxWidth: 480,
        margin: '0 auto',
        background: 'var(--surface-3)',
        color: 'var(--ink-on-green)',
        padding: 'var(--space-base)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-sm)',
      }}
    >
      <div
        id="mfj-cookie-title"
        style={{
          fontFamily: 'var(--font-masthead)',
          fontStyle: 'italic',
          fontSize: '1.125rem',
          textTransform: 'uppercase',
          letterSpacing: '-0.5px',
        }}
      >
        Cookies + storage
      </div>
      <p
        id="mfj-cookie-body"
        style={{
          margin: 0,
          fontSize: 'var(--small)',
          lineHeight: 1.5,
          color: 'rgba(250,245,236,0.85)',
        }}
      >
        We use first-party browser storage to keep you signed in (essential).
        With your OK, we also use Google Analytics to understand which features
        get used — no marketing pixels, no third-party sale of data. Decline
        and we'll skip the analytics. Read the{' '}
        <a
          href="https://myfootballjournal.com/privacy.html"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--gold-light)' }}
        >
          privacy notice
        </a>
        .
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
        <button
          type="button"
          onClick={() => decide('accepted')}
          style={{
            background: 'var(--electric-yellow)',
            color: '#2a1d0c',
            border: 'none',
            padding: '10px 18px',
            borderRadius: 'var(--radius-full)',
            fontFamily: 'var(--font-masthead)',
            fontStyle: 'italic',
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            cursor: 'pointer',
          }}
        >
          OK got it
        </button>
        <button
          type="button"
          onClick={() => decide('declined')}
          style={{
            background: 'transparent',
            color: 'var(--ink-on-green)',
            border: '1px solid rgba(250,245,236,0.45)',
            padding: '10px 18px',
            borderRadius: 'var(--radius-full)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--small)',
            cursor: 'pointer',
          }}
        >
          Decline non-essential
        </button>
      </div>
    </div>
  );
}
